import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { BreadcrumbComponent } from '@app/lib/components/breadcrumb/breadcrumb.component';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Router } from '@angular/router';
import { BaseHttpService } from '@app/lib/services/base-http.service';
import { AppConfiguration } from '@app/lib/services/app-config.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ISearchFieldConfig, ISearchTableConfig, ISearchTableSavePayload, ITableColumnConfig, ITableColumnGroup } from './models/search-table-config.model';
import { SEARCH_TABLE_CONFIGS } from './configs/search-table-configs.constants';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DialogService } from '@app/lib/services/dialog.service';
import { ConfirmationDialogComponent } from '@app/confirmation-dialog/confirmation-dialog.component';
import { MatSort, MatSortModule } from '@angular/material/sort';

@Component({
    selector: 'app-configurable-search-table',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        MatTableModule,
        MatButtonModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatDatepickerModule,
        BreadcrumbComponent,
        MatProgressBarModule,
        MatDialogModule,
        MatSortModule,
    ],
    templateUrl: './configurable-search-table.component.html',
    styleUrl: './configurable-search-table.component.scss'
})
export class ConfigurableSearchTableComponent extends BaseHttpService implements OnInit, AfterViewInit {
    // config is no longer an Input, it will be mapped automatically
    public config!: ISearchTableConfig;

    @Input() searchData: any[] = []; // Parent CAN override data, but by default we fetch it
    @Output() onSearch = new EventEmitter<any>(); // Optional hook for parent
    @Output() onSave = new EventEmitter<ISearchTableSavePayload>(); // Optional hook
    @ViewChild(MatSort) sort!: MatSort;

    searchForm!: FormGroup;
    dataSource = new MatTableDataSource<any>();
    displayedColumns: string[] = [];

    // Track states
    isAddMode = false;
    isEditMode = false;
    selectedRows: any[] = [];
    isLoading = false;
    hasSearched = false;

    // Auto-generated 24-hour time options (HH:mm) for dropdowns
    timeOptions: string[] = [];

    constructor(
        private fb: FormBuilder,
        protected httpClient: HttpClient,
        appConfig: AppConfiguration,
        private router: Router,
        private dialog: MatDialog,
        private dialogService: DialogService
    ) {
        super(httpClient, appConfig);
        this.resolveConfig();
    }

    private showDialog(title: string, message: string): void {
        this.dialogService.openDialog1({
            data: {
                title: title,
                message: message,
                icon: title.toLowerCase().includes('error') ? 'error' :title.toLowerCase().includes('information')?'info':  'check_small'
            },
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'dialog-backdrop',
        });
    }

    resolveConfig(): void {
        const webAddress = this.router.url.split('/').filter(Boolean).pop() || '';
        this.config = SEARCH_TABLE_CONFIGS[webAddress];
        if (!this.config) {
            console.error(`No configuration found for web address: ${webAddress}`);
        }
    }

    ngOnInit(): void {
        if (!this.config) {
            throw new Error('ConfigurableSearchTableComponent requires a config input.');
        }
        this.generateTimeOptions();
        this.initSearchForm();
        this.buildDisplayedColumns();
        this.fetchDropdownOptions();
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        }

    private generateTimeOptions(): void {
        this.timeOptions = [];
        for (let h = 0; h < 24; h++) {
            for (let m = 0; m < 60; m++) {
                const hh = h.toString().padStart(2, '0');
                const mm = m.toString().padStart(2, '0');
                this.timeOptions.push(`${hh}:${mm}`);
            }
        }
    }

    ngOnChanges(): void {
        if (this.searchData) {
            this.dataSource.data = this.searchData;
            this.hasSearched = true;
        }
    }

    initSearchForm(): void {
        const group: any = {};
        this.config.searchFields.forEach((field: ISearchFieldConfig) => {
            const validators = field.required ? [Validators.required] : [];
            if (field.type === 'text') {
                validators;
            }
            const disabledState = { value: '', disabled: !!field.dependableOn };

            if (field.type === 'datetime') {
                group[`${field.key}_date`] = [disabledState, validators];
                group[`${field.key}_time`] = [disabledState, validators];
            } else {
                group[field.key] = [disabledState, validators];
            }
        });
        this.searchForm = this.fb.group(group);
        this.setupSearchFormDependencies();
    }

    private noSpecialCharsValidator(control: any) {
        if (!control.value) {
            return null;
        }
        // Allow alphanumeric and spaces
        const hasSpecialChars = /[^a-zA-Z0-9 ]/.test(control.value);
        return hasSpecialChars ? { 'specialChars': true } : null;
    }

    setupSearchFormDependencies(): void {
        this.config.searchFields.forEach(field => {
            if (field.dependents && field.dependents.length > 0) {
                this.searchForm.get(field.key)?.valueChanges.subscribe(value => {
                    field.dependents?.forEach(dep => {
                        const targetControl = this.searchForm.get(dep.targetKey);
                        if (targetControl) {
                            if (value) {
                                targetControl.enable({ emitEvent: false });
                                targetControl.setValue('');
                                const targetFieldConfig = this.config.searchFields.find(f => f.key === dep.targetKey);
                                if (targetFieldConfig && targetFieldConfig.type === 'select' && targetFieldConfig.endpoint) {
                                    this.loadOptionsForField(targetFieldConfig, { [dep.sourceProperty]: value });
                                }
                            } else {
                                targetControl.disable({ emitEvent: false });
                                targetControl.setValue('');
                                const targetFieldConfig = this.config.searchFields.find(f => f.key === dep.targetKey);
                                if (targetFieldConfig && targetFieldConfig.type === 'select') {
                                    targetFieldConfig.options = [];
                                }
                            }
                        }
                    });
                });
            }
        });
    }

    hasGroups = false;
    topLevelHeaderColumns: string[] = [];
    subLevelHeaderColumns: string[] = [];
    headerGroups: { key: string, name: string, colspan: number }[] = [];

    buildDisplayedColumns(): void {
        this.displayedColumns = ['select'];
        this.topLevelHeaderColumns = ['select'];
        this.subLevelHeaderColumns = [];
        this.headerGroups = [];
        this.hasGroups = false;

        // Check if there are any grouped columns
        this.config.columns.forEach((col: ITableColumnConfig | ITableColumnGroup) => {
            if ('columns' in col) {
                this.hasGroups = true;
            }
        });

        this.config.columns.forEach((col: ITableColumnConfig | ITableColumnGroup, index: number) => {
            if ('columns' in col) {
                // It's a group
                const groupKey = `group_${index}`;
                this.topLevelHeaderColumns.push(groupKey);
                this.headerGroups.push({ key: groupKey, name: col.groupName || '', colspan: col.columns.length });

                col.columns.forEach((subCol: ITableColumnConfig) => {
                    this.subLevelHeaderColumns.push(subCol.key);
                    this.displayedColumns.push(subCol.key);
                });
            } else {
                // Flat column
                this.topLevelHeaderColumns.push(col.key);
                this.displayedColumns.push(col.key);
                // Also add it to subLevelHeaderColumns with a dummy placeholder if we wanted to balance? No, we will use rowspan.
            }
        });
    }

    fetchDropdownOptions(): void {
        // Fetch for Search Fields
        this.config.searchFields.forEach(field => {
            if (field.type === 'select' && field.endpoint && !field.dependableOn) {
                this.loadOptionsForField(field);
            }
        });

        // Fetch for Table Columns
        this.flatColumns.forEach(col => {
            if (col.type === 'select' && col.endpoint && !col.dependableOn) {
                this.loadOptionsForField(col);
            }
        });
    }

    private loadOptionsForField(field: any, extraParams: any = {}): void {
        if (!field.endpoint) return;
        const method = field.endpointMethod || 'GET';

        const req = method === 'GET'
            ? this.get<any>(field.endpoint, { params: extraParams })
            : this.post<any, any>(field.endpoint, extraParams);

        req.subscribe({
            next: (res: any) => {
                const dataArray = res?.success || res?.data || res || [];
                if (Array.isArray(dataArray)) {
                    field.options = dataArray.map((item: any) => {
                        // If it's just an array of strings/numbers
                        if (typeof item !== 'object') {
                            return { value: item, label: String(item) };
                        }

                        let label = item.name || item.label;
                        if (field.endpointLabelKey) {
                            if (field.endpointLabelKey.includes(',')) {
                                const keys = field.endpointLabelKey.split(',').map((k: string) => k.trim());
                                label = keys.map((k: string) => item[k]).filter((val: any) => val !== undefined && val !== null && val !== '').join(' - ');
                            } else {
                                label = item[field.endpointLabelKey];
                            }
                        }

                        return {
                            value: field.endpointValueKey ? item[field.endpointValueKey] : (item.id || item.value),
                            label: label,
                            raw: item
                        };
                    });
                }
            },
            error: (err: any) => {
                console.error(`Failed to fetch options for ${field.key} from ${field.endpoint}`, err);
            }
        });
    }

    allowOnlyValidChars(event: KeyboardEvent): void {
    const pattern = /[a-zA-Z0-9 \-\/]/;
    const inputChar = String.fromCharCode(event.charCode);

    if (!pattern.test(inputChar)) {
        event.preventDefault();
    }
}
    onTableSelectChange(col: any, selectedValue: any, element: any): void {
        if (!col.dependents || col.dependents.length === 0) return;

        const selectedOption = col.options?.find((opt: any) => opt.value === selectedValue);
        if (selectedOption && selectedOption.raw) {
            col.dependents.forEach((dep: any) => {
                if (dep.targetKey && dep.sourceProperty) {
                    element[dep.targetKey] = selectedOption.raw[dep.sourceProperty];
                }
            });
        } else {
            // clear dependents if value is empty/not found
            col.dependents.forEach((dep: any) => {
                if (dep.targetKey) {
                    element[dep.targetKey] = null;
                }
            });
        }
    }

    handleSearch(suppressNotFoundDialog: boolean = false): void {
        let payload = { ...this.searchForm.value };

        // Re-merge datetime fields for search payload
        this.config.searchFields.forEach(field => {
            if (field.type === 'datetime') {
                const dateVal = payload[`${field.key}_date`];
                const timeVal = payload[`${field.key}_time`];
                if (dateVal) {
                    const d = new Date(dateVal);
                    if (timeVal) {
                        const [hh, mm] = timeVal.split(':');
                        d.setHours(parseInt(hh, 10) || 0, parseInt(mm, 10) || 0, 0, 0);
                    }
                    payload[field.key] = d.toISOString();
                }
                delete payload[`${field.key}_date`];
                delete payload[`${field.key}_time`];
            }
        });

        if (this.config.payloadBuilders?.search) {
            payload = this.config.payloadBuilders.search(payload);
        }

        this.onSearch.emit(payload); // Emit for parent if they want to listen

        if (this.config.apiEndpoints?.search) {
            this.isLoading = true;
            const method = this.config.apiEndpoints.searchMethod || 'POST';

            const req = method === 'GET'
                ? this.get<any>(this.config.apiEndpoints.search, { params: payload })
                : this.post<any, any>(this.config.apiEndpoints.search, payload);

            req.subscribe({
                next: (res: any) => {
                    // Assuming standard generic response wrapper `success` or `data` array
                    this.dataSource.data = res?.success || res?.data || res || [];
                    this.isLoading = false;
                    this.isAddMode = false;
                    this.isEditMode = false;
                    this.hasSearched = true;
                    this.selectedRows = [];

                    if (!suppressNotFoundDialog && this.dataSource.data.length === 0) {
                        this.showDialog('Information', 'No Data Exist.');
                    }
                },
                error: (err: any) => {
                    console.error('Dynamic Search Failed', err);
                    this.isLoading = false;
                }
            });
        } else {
            console.warn('Search triggered but no [apiEndpoints.search] was configured in ISearchTableConfig');
            this.isAddMode = false;
            this.isEditMode = false;
            this.hasSearched = true;
            this.selectedRows = [];
        }
    }

    handleReset(): void {
        this.searchForm.reset();
        this.dataSource.data = [];
        this.selectedRows = [];
        this.isAddMode = false;
        this.isEditMode = false;
        this.hasSearched = false;
    }

    handleAdd(): void {
        this.isAddMode = true;
        const newRow: any =this.config.pageTitle==='Document Types'? { _isNew: true, _isEdit: true, activeFlag:'Y',caseCategory:'ALL' }:{ _isNew: true, _isEdit: true };
        this.splitDatetimeFields(newRow);
        this.dataSource.data = [newRow, ...this.dataSource.data];
        this.selectedRows = [newRow];
    }

    handleModify(): void {
        if (this.selectedRows.length === 0) return;
        this.isEditMode = true;
        this.selectedRows.forEach(row => {
            row._isEdit = true;
            this.splitDatetimeFields(row);
        });
    }

    private splitDatetimeFields(row: any): void {
        this.flatColumns.filter((col: any) => col.type === 'datetime').forEach(col => {
            const key = col.key;
            if (row[key]) {
                const d = new Date(row[key]);
                if (!isNaN(d.getTime())) {
                    row[`_${key}_date`] = d;
                    const hh = d.getHours().toString().padStart(2, '0');
                    const mm = d.getMinutes().toString().padStart(2, '0');
                    row[`_${key}_time`] = `${hh}:${mm}`;
                }
            } else {
                row[`_${key}_date`] = null;
                row[`_${key}_time`] = '';
            }
        });
    }

    handleCancel(): void {
        this.isAddMode = false;
        this.isEditMode = false;
        // Strip _isEdit flags
        this.dataSource.data.forEach(row => row._isEdit = false);
        // Remove uncommitted new rows
        this.dataSource.data = this.dataSource.data.filter(row => !row._isNew);
        this.selectedRows = [];
    }

  handleSave(): void {
    let errorMessages: string[] = [];

    // ✅ Mandatory field validation
    for (const row of this.dataSource.data) {
        if (row._isNew || row._isEdit) {
            for (const col of this.flatColumns) {
                if (col.required) {
                    
                    if (col.type === 'datetime') {
                        // For datetime, we must check the two UI-bound temporary keys
                        const dateVal = row[`_${col.key}_date`];
                        const timeVal = row[`_${col.key}_time`];

                        if (!dateVal || !timeVal || (typeof timeVal === 'string' && timeVal.trim() === '')) {
                            errorMessages.push(`${col.label} (Date and Time) is a mandatory field.`);
                        }
                    } else {
                        // Standard validation for all other types
                        const val = row[col.key];
                        if (
                            val === null ||
                            val === undefined ||
                            (typeof val === 'string' && val.trim() === '')
                        ) {
                            errorMessages.push(`${col.label} is a mandatory field.`);
                        }
                    }
                }
            }
        }
    }

    // ✅ Duplicate validation (Updated to handle datetime strings)
    const allRecords = this.dataSource.data;
    const modifyingRecords = allRecords.filter(row => row._isNew || row._isEdit);
    const editableKeys = this.flatColumns
        .filter(c => c.isEditable)
        .map(c => c.key);

    for (const modRow of modifyingRecords) {
        // We temporarily merge to check for duplicates accurately
        this.mergeDatetimeFields(modRow); 

        const isDuplicate = allRecords.some(existingRow => {
            if (existingRow === modRow) return false;
            
            // If it's a datetime column, ensure the existingRow is also merged for comparison
            return editableKeys.every(k => {
                const colConfig = this.flatColumns.find(c => c.key === k);
                if (colConfig?.type === 'datetime') {
                    // Make sure existing rows have a comparable string format
                    this.mergeDatetimeFields(existingRow);
                }
                return existingRow[k] === modRow[k];
            });
        });

        if (isDuplicate) {
            errorMessages.push('Duplicate entry found.');
        }
    }

    // ✅ Remove duplicate messages
    errorMessages = [...new Set(errorMessages)];

    // ✅ Final validation check
    if (errorMessages.length > 0) {
        const errorMessage = errorMessages.join('<br>');
        this.showDialog('Error', errorMessage);
        return;
    }

    // ✅ Confirmation dialog
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
            title: 'Confirm Action',
            message: 'Do you want to Save?',
        },
        disableClose: true,
        hasBackdrop: true,
        backdropClass: 'dialog-backdrop',
    });

    dialogRef.afterClosed().subscribe((result) => {
        if (result) {
            this.executeSave();
        }
    });
}

    private executeSave(): void {
        const newRecs: any[] = [];
        const modRecs: any[] = [];

        this.dataSource.data.forEach(row => {
            // Merge datetime fields before saving
            this.mergeDatetimeFields(row);

            // Strip out internal flags before passing to builders
            const cleanRow = { ...row };
            delete cleanRow._isNew;
            delete cleanRow._isEdit;

            // Delete temp datetime properties
            this.flatColumns.filter((col: any) => col.type === 'datetime').forEach(col => {
                delete cleanRow[`_${col.key}_date`];
                delete cleanRow[`_${col.key}_time`];
            });

            if (row._isNew) newRecs.push(cleanRow);
            else if (row._isEdit) modRecs.push(cleanRow);
        });

        // Use custom builders if provided
        let addPayload = this.config.payloadBuilders?.insert ? this.config.payloadBuilders.insert(newRecs) : newRecs;
        let updatePayload = this.config.payloadBuilders?.update ? this.config.payloadBuilders.update(modRecs) : modRecs;

        const payload: ISearchTableSavePayload = {
            newRecords: addPayload,
            modifiedRecords: updatePayload,
            deletedRecords: []
        };
        this.onSave.emit(payload); // Let parent hook in if they want to

        // Standard save endpoint
        if (this.config.apiEndpoints?.save) {
            let combinedPayload = payload;
            if (this.config.payloadBuilders?.save) {
                combinedPayload = this.config.payloadBuilders.save(newRecs, modRecs, []);
            }
            this.executeApiCall(this.config.apiEndpoints.save, combinedPayload, this.config.apiEndpoints.saveMethod, 'Save');
        } else {
            // Separate Insert/Update endpoints
            if (this.config.apiEndpoints?.add && newRecs.length > 0) {
                this.executeApiCall(this.config.apiEndpoints.add, addPayload, this.config.apiEndpoints.addMethod, 'Save');
            }
            if (this.config.apiEndpoints?.update && modRecs.length > 0) {
                this.executeApiCall(this.config.apiEndpoints.update, updatePayload, this.config.apiEndpoints.updateMethod, 'Save');
            }
        }
    }

    private mergeDatetimeFields(row: any): void {
    this.flatColumns.filter((col: any) => col.type === 'datetime').forEach(col => {
        const key = col.key;
        const dateVal = row[`_${key}_date`];
        const timeVal = row[`_${key}_time`];

        if (dateVal) {
            const d = new Date(dateVal);
            if (timeVal) {
                const [hh, mm] = timeVal.split(':');
                d.setHours(parseInt(hh, 10) || 0, parseInt(mm, 10) || 0, 0, 0);
                
                // Using .toString() instead of .toISOString() 
                // to get the "Sun Feb 09 2026..." format
                row[key] = d.toString(); 
            } else {
                // If date is there but no time yet, we can't fully merge 
                // or you can default to 00:00:00
                row[key] = d.toString(); 
            }
        } else {
            row[key] = null;
        }
    });
}

    handleDelete(): void {
        if (this.selectedRows.length === 0) return;

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Confirm Action',
                message: 'Do you want to Delete?',
            },
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'dialog-backdrop',
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.executeDelete();
            }
        });
    }

    private executeDelete(): void {
        const delRecs = this.selectedRows.map(row => {
            const cleanRow = { ...row };
            delete cleanRow._isNew;
            delete cleanRow._isEdit;
            return cleanRow;
        });

        let deletePayload = this.config.payloadBuilders?.delete ? this.config.payloadBuilders.delete(delRecs) : delRecs;

        this.onSave.emit({
            newRecords: [],
            modifiedRecords: [],
            deletedRecords: deletePayload
        });

        if (this.config.apiEndpoints?.delete && delRecs.length > 0) {
            this.executeApiCall(this.config.apiEndpoints.delete, deletePayload, this.config.apiEndpoints.deleteMethod, 'Delete');
        } else {
            console.warn('Delete triggered but no [apiEndpoints.delete] was configured');
            this.handleCancel();
        }
    }

    private executeApiCall(endpoint: string, payload: any, method: 'GET' | 'POST' = 'POST', actionName?: string): void {
        this.isLoading = true;
        const req = method === 'GET'
            ? this.get<any>(endpoint, { params: payload })
            : this.post<any, any>(endpoint, payload);

        req.subscribe({
            next: (res: any) => {
                this.isLoading = false;
                this.isAddMode = false;
                this.isEditMode = false;
                this.selectedRows = [];
                // Re-trigger search to fetch updated DB mapping
                this.handleSearch(true);

                if (actionName) {
                    if (res?.success !== false) {
                        this.showDialog('Success',res?.message || `${actionName} completed successfully.`);
                    } else {
                        this.showDialog('Error', res?.message || `${actionName}  failed.`);
                    }
                }
            },
            error: (err: any) => {
                console.error('Dynamic API Failed', err);
                this.isLoading = false;
                if (actionName) {
                    this.showDialog('Error', `${actionName}  failed.`);
                }
            }
        });
    }

    toggleSelection(row: any, checked: boolean): void {
        if (checked) {
            this.selectedRows = [row];
        } else {
            this.selectedRows = this.selectedRows.filter(r => r !== row);
        }
    }

    isSelected(row: any): boolean {
        return this.selectedRows.includes(row);
    }

    // To help the template iterate properly
    get flatColumns(): ITableColumnConfig[] {
        const cols: ITableColumnConfig[] = [];
        this.config.columns.forEach((col: ITableColumnConfig | ITableColumnGroup) => {
            if ('columns' in col) {
                cols.push(...col.columns);
            } else {
                cols.push(col as ITableColumnConfig);
            }
        });
        return cols;
    }
}
